// /accept-invite?token=… — link from the invitation mail
// (send_invitation_email). Creates the account with
// POST /users/invitations/accept { token, full_name, password }.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { post } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import { MIN_PASSWORD } from '../../lib/auth.jsx';
import { useLang, useT } from '../../lib/i18n.jsx';
import PublicCard, { Notice } from './PublicCard.jsx';

// The API's 400 details for this endpoint, mapped to our own texts.
function inviteError(e, t, lang) {
  const detail = String(e?.message || '');
  if (e?.status === 400) {
    if (/expired/i.test(detail)) return t('invite.expired');
    if (/already registered/i.test(detail)) return t('invite.registered');
    return t('invite.invalid');
  }
  return errorMessage(e, { lang });
}

export default function AcceptInvite() {
  const t = useT();
  const { lang } = useLang();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // created user
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < MIN_PASSWORD) return setErr(t('public.password.tooShort', { n: MIN_PASSWORD }));
    if (pw !== pw2) return setErr(t('public.password.mismatch'));
    setBusy(true);
    try {
      const user = await post('/users/invitations/accept', { token, full_name: name.trim(), password: pw });
      setDone(user || {});
    } catch (e2) {
      setErr(inviteError(e2, t, lang));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <PublicCard title={t('invite.title')}>
        <Notice tone="error">{t('public.missingToken')}</Notice>
      </PublicCard>
    );
  }

  if (done) {
    return (
      <PublicCard title={t('invite.title')}>
        <Notice>{done.email ? t('invite.done.email', { email: done.email }) : t('invite.done')}</Notice>
        <Link className="btn-primary public-cta" to="/login">{t('public.toLogin')}</Link>
      </PublicCard>
    );
  }

  return (
    <PublicCard title={t('invite.title')} subtitle={t('invite.sub')}>
      <form className="login-form" onSubmit={submit}>
        {err && <Notice tone="error">{err}</Notice>}
        <div>
          <label htmlFor="name">{t('invite.name')}</label>
          <input id="name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="pw">{t('account.password')}</label>
          <input id="pw" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} value={pw} onChange={(e) => setPw(e.target.value)} />
          <p className="field-hint">{t('public.password.hint', { n: MIN_PASSWORD })}</p>
        </div>
        <div>
          <label htmlFor="pw2">{t('public.password.repeat')}</label>
          <input id="pw2" type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </div>
        <div className="login-actions">
          <span />
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('account.working') : t('invite.submit')}
          </button>
        </div>
      </form>
    </PublicCard>
  );
}
